import type { ZodType } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';

export type ApiFetchFn = typeof import('./requests')['apiFetch'];

export const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

export const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());

export const serializeBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return body;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body;
  }
  return JSON.stringify(body);
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type JsonRequestOptions<T> = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  schema: ZodType<T>;
  tenantSlug?: string;
};

export const jsonRequest = async <T>(path: string, options: JsonRequestOptions<T>): Promise<T> => {
  const apiFetch = await resolveApiFetch();
  const { method = 'GET', body, signal, tenantSlug, headers = {}, schema } = options;

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };

  if (tenantSlug && !hasHeader(requestHeaders, 'x-tenant')) {
    requestHeaders['X-Tenant'] = tenantSlug;
  }

  const serialisedBody = serializeBody(body);
  if (serialisedBody !== undefined && typeof serialisedBody === 'string' && !hasHeader(requestHeaders, 'content-type')) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: serialisedBody,
    signal,
    headers: requestHeaders,
  });

  if (response.status === 204) {
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, schema);
};
